import type { FC } from 'react';
import { useCallback, useContext, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import {
  GenericRequestStatusType,
  PresetsInitResponse,
} from '@wix/da-submit-shared/pkg/redux/types';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import DesktopBatchActionWrapper from '../DesktopBatchActionWrapper';
import MobileBatchActionWrapper from '../MobileBatchActionWrapper';
import { BatchActionProps } from '../types';

import s from './PresetBatchEdit.scss';

export interface Props extends BatchActionProps {
  status: GenericRequestStatusType;
  initResponse?: PresetsInitResponse;
  fetchPresets: () => void;
  batchEditPreset: (presetId: number) => void;
}

const PresetBatchEdit: FC<Props> = ({
  selectionArea,
  status,
  initResponse,
  fetchPresets,
  batchEditPreset,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const [presetId, setPresetId] = useState<number | null>(null);
  const presetIdIsValid = presetId === 0 || parseInt(`${presetId}`) > 0;
  const { presets = [] } = initResponse || {};
  const presetInputId = useId();

  const handleSave = useCallback(() => {
    presetIdIsValid && presetId !== null && batchEditPreset(presetId);
  }, [batchEditPreset, presetId, presetIdIsValid]);

  useEffect(() => {
    if (status === 'uninitialized') {
      fetchPresets();
    }
  }, [status, fetchPresets]);

  const form =
    status === 'uninitialized' || status === 'fetching' ? (
      <LoadingIndicator view={'llama'} className={s['loader']} />
    ) : (
      <div>
        <SelectField
          id={presetInputId}
          items={presets.map(preset => ({
            value: preset.presetId,
            label: preset.presetName || t('studio.modals.presets.untitled'),
            selected: presetId === preset.presetId,
          }))}
          onSelect={value => setPresetId(parseInt(value))}
          value={`${presetId}`}
          label={t('studio.batch.preset.label')}
          isLabelHidden
        />
      </div>
    );

  if (isMobile) {
    return (
      <MobileBatchActionWrapper
        heading={t('studio.batchActions.subtitle.edit_preset')}
        onSave={handleSave}
        disableSave={!presetIdIsValid}
        restrictSave="non_core_multiple"
      >
        {form}
      </MobileBatchActionWrapper>
    );
  }
  return (
    <DesktopBatchActionWrapper
      heading={t('studio.batchActions.subtitle.edit_preset')}
      selectionArea={selectionArea}
      onSave={handleSave}
      disableSave={!presetIdIsValid}
      restrictSave="non_core_multiple"
    >
      {form}
    </DesktopBatchActionWrapper>
  );
};
PresetBatchEdit.displayName = 'PresetBatchEdit';

export default PresetBatchEdit;
